import React from 'react';
import {
  Row,
  RowAvatarSlot,
  Avatar,
  Bubble,
  SenderLabel,
  Meta,
  ReadTick,
  SystemNote,
  MediaImage,
  MediaVideo,
  FileCard,
  FileIcon,
  FileMeta,
  CustomButton,
} from './styles';
import PdfBubbleV2 from './PdfBubbleV2';
import ChatManager from '../../services/ChatManager';
import {
  initialsOf,
  bubbleTime,
  dirFor,
  fileKind,
  linkifyParts,
  avatarColor,
  DELETED_AVATAR,
} from './helpers';
import { FileIcon as FileGlyph, CheckIcon } from './icons';

const renderText = (text) =>
  linkifyParts(text).map((p, i) =>
    p.type === 'link' ? (
      <a key={i} href={p.href} target="_blank" rel="noopener noreferrer">
        {p.value}
      </a>
    ) : (
      <React.Fragment key={i}>{p.value}</React.Fragment>
    ),
  );

const MessageBubbleV2 = ({
  message,
  showAvatar,
  grouped,
  isGroup,
  onOpenImage,
  onCustomAction,
}) => {
  // Compute ownership at render time from the current user id (timing-proof),
  // falling back to the value baked in at map time.
  const owned = ChatManager.myUserId
    ? message.user?.id === ChatManager.myUserId
    : message.isOwned;
  const kind = fileKind(message);

  // System (join/left) — centered note. The server stores the bare verb
  // ("joined"/"left") and the actor as the message sender, so the name has to be
  // put back here; without it the note says something happened but not to whom.
  if (kind === 'system') {
    const who = message.user?.name;
    return <SystemNote>{who ? `${who} ${message.text}` : message.text}</SystemNote>;
  }

  const renderContent = () => {
    switch (kind) {
      case 'image':
        return (
          <MediaImage
            src={message.attachment}
            alt=""
            onClick={() => onOpenImage(message.attachment)}
          />
        );
      case 'video':
        return (
          <MediaVideo controls>
            <source src={message.attachment} type="video/mp4" />
          </MediaVideo>
        );
      case 'pdf':
        return (
          <PdfBubbleV2 url={message.attachment} name={message.attachmentName} owned={owned} />
        );
      case 'file':
        return (
          <FileCard
            owned={owned}
            href={message.attachment}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FileIcon>
              <FileGlyph size={20} />
            </FileIcon>
            <FileMeta>
              <div className="name">{message.attachmentName || 'Open file'}</div>
              <div className="sub">Tap to open</div>
            </FileMeta>
          </FileCard>
        );
      case 'custom': {
        const action = message.customData && message.customData.action;
        const icon = message.customData && message.customData.icon;
        const label = (action && action.label) || message.text;
        return (
          <CustomButton
            clickable={!!action}
            onClick={action ? () => onCustomAction(message) : undefined}
          >
            {icon && <img src={icon} alt="" />}
            <span>{label}</span>
          </CustomButton>
        );
      }
      default:
        return renderText(message.text);
    }
  };

  // These render their own surface, so the bubble must not add padding/background on top.
  const mediaLike = kind === 'image' || kind === 'video' || kind === 'pdf';

  // A purged sender has no uid left — the API nulls sender_uid and resolves the
  // name to "Deleted user". Colouring by the label would give every deleted member
  // an identical avatar, so use a neutral one and drop the initials.
  const deletedSender = !owned && !message.user?.id;
  const senderColor = deletedSender
    ? DELETED_AVATAR
    : avatarColor(message.user?.id || message.user?.name);

  return (
    <Row owned={owned} grouped={grouped}>
      <RowAvatarSlot>
        {!owned && showAvatar && (
          <Avatar
            src={deletedSender ? undefined : message.user?.avatar}
            bg={senderColor.bg}
            fg={senderColor.fg}
            size="3.2rem"
            title={deletedSender ? 'Deleted user' : message.user?.name}
          >
            {deletedSender ? '–' : !message.user?.avatar && initialsOf(message.user?.name)}
          </Avatar>
        )}
      </RowAvatarSlot>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: owned ? 'flex-end' : 'flex-start',
          maxWidth: '72%',
          minWidth: 0,
        }}
      >
        {isGroup && !owned && showAvatar && (
          <SenderLabel style={{ color: senderColor.fg }}>
            {message.user?.name}
          </SenderLabel>
        )}
        <Bubble
          owned={owned}
          grouped={grouped}
          dir={dirFor(message.text)}
          style={mediaLike ? { padding: '0.4rem', background: 'transparent', boxShadow: 'none' } : undefined}
        >
          {renderContent()}
        </Bubble>
        <Meta owned={owned}>
          {bubbleTime(message.sentAt)}
          {owned && (
            <ReadTick title="Sent">
              <CheckIcon size={14} />
            </ReadTick>
          )}
        </Meta>
      </div>
    </Row>
  );
};

export default MessageBubbleV2;
